import { motion } from "framer-motion";
import { EASE, VIEWPORT, reveal } from "@/lib/motion";
import { WhatsAppButton } from "./WhatsAppButton";
import { TextReveal } from "@/components/ui/TextReveal";
import { Sparkles } from "lucide-react";
import packingImg from "@/assets/packing.png";

const occasions = [
  { name: "Birthdays", tag: "Custom Tins" },
  { name: "Festive Hampers", tag: "Holiday Bakes" },
  { name: "Plain Cakes", tag: "Fresh Baked" },
  { name: "Rakhi Boxes", tag: "Festive Pack" },
  { name: "Bulk Orders", tag: "Special Discounts" },
];

export default function Gifting() {
  return (
    <section
      id="gifting"
      data-testid="gifting-section"
      className="relative py-12 sm:py-16 md:py-20 bg-[#F2EBE0] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-center">
          {/* Left Column: Packing Image */}
          <motion.div
            {...reveal(0, { y: 20 })}
            className="col-span-12 lg:col-span-5 relative"
          >
            <div className="relative aspect-[16/10] sm:aspect-[4/3] lg:aspect-[4/4.8] rounded-[24px] sm:rounded-[28px] overflow-hidden border border-[#3B2818]/15 shadow-md grain">
              <img
                src={packingImg}
                alt="Signature Spoon Gifting Packing"
                loading="lazy"
                className="w-full h-full object-cover transform-gpu transition-transform duration-500 hover:scale-105"
              />
              <div className="absolute bottom-4 left-4 bg-[#3B2818]/85 backdrop-blur-md text-[#FAF6F0] rounded-full px-4 py-2 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-medium flex items-center gap-2 border border-[#FAF6F0]/20 shadow-lg">
                <Sparkles className="h-3.5 w-3.5 text-[#C06A22]" />
                <span>Hand-tied Gift Boxes</span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Arranged Blocks & Text */}
          <div className="col-span-12 lg:col-span-7 flex flex-col justify-between">
            <motion.div {...reveal(0.05)}>
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#C06A22] font-semibold">
                Gifting & Seasonal
              </span>
              <TextReveal className="font-serif text-[28px] sm:text-[36px] md:text-5xl lg:text-6xl leading-[1.05] mt-2 text-[#3B2818]">
                Sweetness,{" "}
                <span className="italic font-light text-[#C06A22]">
                  wrapped and ready.
                </span>
              </TextReveal>
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm md:text-base leading-relaxed text-[#6B5343] max-w-xl">
                Custom hampers for birthdays, festive tins for Diwali and
                Christmas, bulk celebration orders, and thoughtful little boxes
                for the people you care about.
              </p>
            </motion.div>

            {/* Occasions Blocks */}
            <div className="mt-6 sm:mt-8 pt-6 border-t border-[#3B2818]/12">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#3B2818]/60 font-semibold mb-3">
                Popular Gifting Occasions
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-2.5 sm:gap-3">
                {occasions.map((o, i) => (
                  <motion.div
                    key={o.name}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={VIEWPORT}
                    transition={{ duration: 0.35, delay: i * 0.03, ease: EASE }}
                    className="bg-[#FAF6F0] rounded-xl sm:rounded-2xl p-3 border border-[#3B2818]/10 hover:border-[#C06A22]/40 transition-colors"
                  >
                    <div className="text-[9px] sm:text-[10px] uppercase tracking-wider text-[#C06A22] font-medium">
                      {o.tag}
                    </div>
                    <div className="font-serif text-sm sm:text-base text-[#3B2818] font-medium mt-0.5 leading-snug">
                      {o.name}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <motion.div
              {...reveal(0.15)}
              className="mt-6 sm:mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-[#3B2818]/12"
            >
              <WhatsAppButton
                variant="primary"
                size="lg"
                label="Plan a gifting order"
                testId="gifting-wa-cta"
                msg="Hi! I'd like to plan a gifting/festive order."
              />
              <WhatsAppButton
                variant="ghost"
                size="lg"
                label="Ask about bulk"
                testId="gifting-bulk-cta"
                msg="Hi! I'd like to enquire about bulk orders."
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
